import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, FileText, CheckCircle2 } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import {
  feeCycleCreateSchema,
  feePaymentBulkMarkPaidSchema,
  type FeeCycleInput,
} from '@bbl/validation';
import { z } from 'zod';
import { apiRequest, ApiError, type ListResponse, openApiDocument } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { useToast } from '../lib/toast';
import { formatINR, formatDate } from '../lib/format';
import { ListShell } from '../components/ListShell';
import { Button } from '../components/ui/Button';
import { Drawer } from '../components/ui/Drawer';
import { DataTable } from '../components/ui/DataTable';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { TextField, SelectField, TextAreaField } from '../components/ui/Field';
import { EditIconButton } from '../components/shared/EditIconButton';
import { ListOrOutlet } from '../components/shared/ListOrOutlet';
import { Pagination } from '../components/people/Pagination';
import { FeeStatusBadge } from '../components/finance/StatusBadge';
import { CycleSidebar, CycleSelect } from '../components/finance/CycleSidebar';
import { MarkPaidDrawer } from '../components/finance/MarkPaidDrawer';
import { normalisePaymentRow } from '../components/finance/normalisers';
import {
  FilterBar,
  SortableHeader,
  useListFilters,
  type FilterRoute,
} from '../components/filters';
import {
  feesPaymentsFilterConfig,
  feesPaymentsSearchSchema,
  FEES_PAYMENTS_SORT_COLUMNS,
} from '../components/finance/feesPaymentsFilterConfig';
import {
  PAID_VIA_OPTIONS,
  cycleLabel,
  type FeeCycle,
  type FeePayment,
  type FeePaymentJoined,
} from '../components/finance/types';

const PAGE_SIZE = 20;

/** A loaded page of the per-cycle payments grid: normalised rows + the total. */
interface FeePaymentsPage {
  items: FeePaymentJoined[];
  total: number;
}

export const Route = createFileRoute('/_authed/finance/fees')({
  validateSearch: feesPaymentsSearchSchema,
  component: FeesPage,
});

function FeesPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.primaryRole ?? null);
  const canManage = role === 'super_admin' || role === 'finance_admin';
  const [selectedCycleId, setSelectedCycleId] = useState<string | null>(null);
  const [newCycleOpen, setNewCycleOpen] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());
  const [markPaidPayment, setMarkPaidPayment] = useState<FeePayment | null>(null);
  const [bulkPaidOpen, setBulkPaidOpen] = useState(false);

  // The single source of truth for search / filters / sort / page WITHIN the
  // selected cycle, backed by the route's URL search params. The selected cycle
  // itself stays local state (the cycle picker) — it is NOT a toolkit facet. The
  // adapter narrows the route's precisely-typed search/navigate surface to the
  // toolkit's structural `FilterRoute` contract; the route's own Zod
  // `validateSearch` keeps the boundary typed.
  const filterRoute: FilterRoute = {
    useSearch: () => Route.useSearch() as Record<string, unknown>,
    useNavigate: () => {
      const navigateRoute = Route.useNavigate();
      return (opts) =>
        navigateRoute({
          search: ((prev: Record<string, unknown>) => opts.search(prev)) as never,
          ...(opts.replace !== undefined ? { replace: opts.replace } : {}),
        });
    },
  };
  const filters = useListFilters(feesPaymentsFilterConfig, filterRoute, {
    pageSize: PAGE_SIZE,
  });
  const { values, apiParams, setSort, setPage } = filters;

  const cyclesQuery = useQuery({
    queryKey: ['feeCycles'] as const,
    queryFn: async ({ signal }) => {
      const res = await apiRequest<{ items: FeeCycle[] }>('/api/fees/cycles', { signal });
      return res.items;
    },
  });

  useEffect(() => {
    if (selectedCycleId) return;
    const first = cyclesQuery.data?.[0];
    if (first) setSelectedCycleId(first.id);
  }, [cyclesQuery.data, selectedCycleId]);

  const paymentsQuery = useQuery<FeePaymentsPage>({
    // The cycle id is the selector, merged with the toolkit's emitted facet /
    // sort / page params. Both belong in the key so a change to either refetches.
    queryKey: ['feePayments', selectedCycleId, apiParams] as const,
    enabled: !!selectedCycleId,
    placeholderData: (prev) => prev,
    queryFn: async ({ signal }): Promise<FeePaymentsPage> => {
      if (!selectedCycleId) return { items: [], total: 0 };
      const res = await apiRequest<Partial<ListResponse<unknown>>>(
        '/api/fees/payments',
        { signal, query: { cycleId: selectedCycleId, ...apiParams } },
      );
      // The endpoint returns the joined shape ({ payment, student, cycle }); the
      // resilient normaliser also tolerates legacy raw rows. We read `.total`
      // for the result count + pagination and keep `.items` as the grid rows.
      return {
        items: (res.items ?? []).map(normalisePaymentRow),
        total: typeof res.total === 'number' ? res.total : 0,
      };
    },
  });

  // Clear selection when the listed page changes — i.e. on any cycle, filter,
  // sort or page change (`apiParams` is memoised on the decoded URL state, so it
  // re-fires for exactly those). This keeps bulk-select scoped to the rows
  // currently loaded, matching the prior per-cycle/status semantics.
  useEffect(() => {
    setSelectedRowIds(new Set());
  }, [selectedCycleId, apiParams]);

  const rows: FeePaymentJoined[] = paymentsQuery.data?.items ?? [];
  const total = paymentsQuery.data?.total ?? 0;

  const selectedCycle = cyclesQuery.data?.find((c) => c.id === selectedCycleId) ?? null;

  const toggleRow = (id: string) => {
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectablePending = rows.filter((r) => r.payment.status !== 'paid');
  const allPendingSelected =
    selectablePending.length > 0 &&
    selectablePending.every((r) => selectedRowIds.has(r.payment.id));

  const toggleSelectAll = () => {
    if (allPendingSelected) {
      setSelectedRowIds(new Set());
    } else {
      setSelectedRowIds(new Set(selectablePending.map((r) => r.payment.id)));
    }
  };

  const openReceipt = async (paymentId: string) => {
    try {
      await openApiDocument(`/api/fees/payments/${encodeURIComponent(paymentId)}/receipt-url`);
    } catch (err) {
      if (err instanceof ApiError) toast(err.message, { variant: 'error' });
      else toast('Could not open receipt', { variant: 'error' });
    }
  };

  const columns = useMemo<ColumnDef<FeePaymentJoined, unknown>[]>(
    () => [
      {
        id: 'select',
        header: () => (
          <input
            type="checkbox"
            aria-label="Select all unpaid"
            checked={allPendingSelected}
            onChange={toggleSelectAll}
            disabled={selectablePending.length === 0}
            className="h-4 w-4 accent-gold-700"
          />
        ),
        cell: ({ row }) => {
          const p = row.original.payment;
          const isPaid = p.status === 'paid';
          return (
            <input
              type="checkbox"
              aria-label={`Select payment for ${row.original.student.name}`}
              checked={selectedRowIds.has(p.id)}
              onChange={(e) => {
                e.stopPropagation();
                toggleRow(p.id);
              }}
              onClick={(e) => e.stopPropagation()}
              disabled={isPaid}
              className="h-4 w-4 accent-gold-700"
            />
          );
        },
        enableSorting: false,
        size: 40,
      },
      {
        id: 'student',
        accessorFn: (r) => r.student.name,
        header: 'Student',
        cell: ({ row }) => (
          <div className="min-w-0">
            <div className="font-medium truncate">{row.original.student.name}</div>
            {row.original.student.admissionNumber ? (
              <div className="text-xs text-[color:var(--color-text-muted)] truncate">
                {row.original.student.admissionNumber}
              </div>
            ) : null}
          </div>
        ),
      },
      {
        id: 'amount',
        header: 'Amount',
        accessorFn: (r) => r.payment.amountPaise,
        cell: ({ row }) => formatINR(row.original.payment.amountPaise),
      },
      {
        id: 'discount',
        header: 'Discount',
        accessorFn: (r) => r.payment.discountPaise,
        cell: ({ row }) =>
          row.original.payment.discountPaise > 0
            ? formatINR(row.original.payment.discountPaise)
            : '—',
      },
      {
        id: 'net',
        header: 'Net',
        accessorFn: (r) => r.payment.netAmountPaise,
        cell: ({ row }) => (
          <span className="font-medium">
            {formatINR(row.original.payment.netAmountPaise)}
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        accessorFn: (r) => r.payment.status,
        cell: ({ row }) => <FeeStatusBadge status={row.original.payment.status} />,
      },
      {
        id: 'dueDate',
        accessorFn: (r) => r.payment.dueDate,
        enableSorting: false,
        header: () => (
          <SortableHeader
            label="Due"
            binding={FEES_PAYMENTS_SORT_COLUMNS.dueDate}
            currentSort={values.sort}
            onSort={setSort}
          />
        ),
        cell: ({ row }) => formatDate(`${row.original.payment.dueDate}T00:00:00Z`),
      },
      {
        id: 'paidAt',
        accessorFn: (r) => r.payment.paidAt ?? '',
        enableSorting: false,
        header: () => (
          <SortableHeader
            label="Paid on"
            binding={FEES_PAYMENTS_SORT_COLUMNS.paidAt}
            currentSort={values.sort}
            onSort={setSort}
          />
        ),
        cell: ({ row }) =>
          row.original.payment.paidAt
            ? formatDate(row.original.payment.paidAt, { withTime: true })
            : '—',
      },
      {
        id: 'receipt',
        header: 'Receipt',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.payment.status === 'paid' ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                void openReceipt(row.original.payment.id);
              }}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-gold-700 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500 rounded"
            >
              <FileText size={14} aria-hidden="true" />
              View
            </button>
          ) : (
            <span className="text-xs text-[color:var(--color-text-muted)]">—</span>
          ),
      },
      ...(canManage
        ? ([
            {
              id: 'edit',
              header: '',
              enableSorting: false,
              size: 48,
              cell: ({ row }) => {
                const p = row.original.payment;
                if (p.status === 'paid') {
                  return <span aria-hidden="true" className="block" />;
                }
                return (
                  <div className="flex justify-end">
                    <EditIconButton
                      label="Mark paid"
                      title="Mark paid"
                      onClick={() => setMarkPaidPayment(p)}
                    />
                  </div>
                );
              },
            },
          ] satisfies ColumnDef<FeePaymentJoined, unknown>[])
        : []),
    ],
    // We rebuild columns when selection state, row identity, totals, and the
    // active sort change so the header checkbox + the sortable Due/Paid headers
    // stay in sync with the visible rows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedRowIds, allPendingSelected, selectablePending.length, canManage, values.sort, setSort],
  );

  const bulkSelectionCount = selectedRowIds.size;

  return (
    <ListOrOutlet basePath="/finance/fees">
    <ListShell
      title="Fees"
      description="Monthly cycles and payment status."
      primaryAction={
        <Button icon={<Plus size={16} />} onClick={() => setNewCycleOpen(true)}>
          New cycle
        </Button>
      }
    >
      {/* Mobile cycle selector */}
      <div className="md:hidden">
        <CycleSelect
          cycles={cyclesQuery.data ?? []}
          selectedId={selectedCycleId}
          onSelect={setSelectedCycleId}
          ariaLabel="Select fee cycle"
        />
      </div>

      <div className="grid gap-5 md:grid-cols-[260px_minmax(0,1fr)]">
        <aside className="hidden md:block min-w-0">
          <h2 className="px-1 text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)] mb-2">
            Cycles
          </h2>
          {cyclesQuery.error ? (
            <ErrorState
              title="Could not load cycles"
              onRetry={() => cyclesQuery.refetch()}
            />
          ) : (
            <CycleSidebar
              cycles={cyclesQuery.data ?? []}
              selectedId={selectedCycleId}
              onSelect={setSelectedCycleId}
              isLoading={cyclesQuery.isLoading}
              dateLabel={(c) => `Due ${formatDate(`${c.dueDate}T00:00:00Z`)}`}
              emptyLabel="No cycles yet. Create one to get started."
              renderLink={(c, { ariaLabel }) => (
                <Link
                  to="/finance/fees/$cycleId"
                  params={{ cycleId: c.id }}
                  aria-label={ariaLabel}
                  className="absolute inset-0 rounded-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500"
                />
              )}
            />
          )}
        </aside>

        <section className="min-w-0 space-y-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <h2 className="font-display text-xl font-semibold truncate">
                {selectedCycle
                  ? cycleLabel(selectedCycle.year, selectedCycle.month)
                  : 'Select a cycle'}
              </h2>
              {selectedCycle ? (
                <p className="text-xs text-[color:var(--color-text-muted)]">
                  Due {formatDate(`${selectedCycle.dueDate}T00:00:00Z`)}
                </p>
              ) : null}
            </div>
            {bulkSelectionCount > 0 ? (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  icon={<CheckCircle2 size={14} />}
                  onClick={() => setBulkPaidOpen(true)}
                >
                  Mark {bulkSelectionCount} paid
                </Button>
              </div>
            ) : null}
          </div>

          <FilterBar
            config={feesPaymentsFilterConfig}
            filters={filters}
            resultCount={total}
          />

          {paymentsQuery.error ? (
            <ErrorState
              title="Could not load payments"
              onRetry={() => paymentsQuery.refetch()}
            />
          ) : (
            <>
              <DataTable<FeePaymentJoined>
                data={rows}
                columns={columns}
                isLoading={paymentsQuery.isLoading}
                onRowClick={(r) =>
                  void navigate({
                    to: '/finance/fees/payments/$paymentId',
                    params: { paymentId: r.payment.id },
                  })
                }
                getRowId={(r) => r.payment.id}
                emptyState={
                  <EmptyState
                    title={selectedCycleId ? 'No payments match these filters' : 'Pick a cycle'}
                    description={
                      selectedCycleId
                        ? 'Try clearing a filter to see more payment records.'
                        : 'Choose a cycle from the list to view its payments.'
                    }
                  />
                }
              />
              {selectedCycleId ? (
                <Pagination
                  page={values.page}
                  pageSize={PAGE_SIZE}
                  total={total}
                  onPageChange={setPage}
                />
              ) : null}
            </>
          )}
        </section>
      </div>

      <NewCycleDrawer
        open={newCycleOpen}
        onClose={() => setNewCycleOpen(false)}
        onCreated={async (cycleId, created) => {
          await queryClient.invalidateQueries({ queryKey: ['feeCycles'] });
          setSelectedCycleId(cycleId);
          toast(`Cycle created and ${created} records seeded.`, { variant: 'success' });
        }}
      />

      <MarkPaidDrawer
        payment={markPaidPayment}
        onClose={() => setMarkPaidPayment(null)}
        onMarked={async () => {
          await queryClient.invalidateQueries({ queryKey: ['feePayments'] });
          toast('Payment marked as paid.', { variant: 'success' });
          setMarkPaidPayment(null);
        }}
      />

      <BulkMarkPaidDrawer
        open={bulkPaidOpen}
        paymentIds={Array.from(selectedRowIds)}
        onClose={() => setBulkPaidOpen(false)}
        onMarked={async (count) => {
          await queryClient.invalidateQueries({ queryKey: ['feePayments'] });
          setSelectedRowIds(new Set());
          setBulkPaidOpen(false);
          toast(`${count} payments marked as paid.`, { variant: 'success' });
        }}
      />
    </ListShell>
    </ListOrOutlet>
  );
}

// ---------- New cycle drawer ----------

const currentYear = () => new Date().getFullYear();
const currentMonth = () => new Date().getMonth() + 1;
const todayIsoDate = () => new Date().toISOString().slice(0, 10);

const newCycleFormSchema = feeCycleCreateSchema;
type NewCycleFormValues = FeeCycleInput;

interface NewCycleDrawerProps {
  open: boolean;
  onClose: () => void;
  onCreated: (cycleId: string, created: number) => Promise<void> | void;
}

function NewCycleDrawer({ open, onClose, onCreated }: NewCycleDrawerProps) {
  const { toast } = useToast();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<NewCycleFormValues>({
    resolver: zodResolver(newCycleFormSchema),
    defaultValues: {
      year: currentYear(),
      month: currentMonth(),
      dueDate: todayIsoDate(),
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        year: currentYear(),
        month: currentMonth(),
        dueDate: todayIsoDate(),
      });
    }
  }, [open, reset]);

  const onSubmit = async (values: NewCycleFormValues) => {
    try {
      const created = await apiRequest<{ id: string }>('/api/fees/cycles', {
        method: 'POST',
        body: values,
      });
      let recordsCreated = 0;
      try {
        const gen = await apiRequest<{ ok: true; created: number }>(
          `/api/fees/cycles/${encodeURIComponent(created.id)}/generate-records`,
          { method: 'POST' },
        );
        recordsCreated = gen.created;
      } catch (err) {
        // Cycle exists even if generation failed; surface but proceed.
        if (err instanceof ApiError) toast(err.message, { variant: 'error' });
        else toast('Cycle created, but record generation failed.', { variant: 'error' });
      }
      await onCreated(created.id, recordsCreated);
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'cycle_exists') {
          setError('month', { message: 'A cycle already exists for this month' });
        }
        toast(err.message, { variant: 'error' });
      } else {
        toast('Could not create cycle.', { variant: 'error' });
      }
    }
  };

  return (
    <Drawer open={open} onClose={onClose} title="New fees cycle" size="md">
      <form
        id="new-fee-cycle-form"
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-4"
        noValidate
      >
        <div className="grid grid-cols-2 gap-3">
          <TextField
            label="Year"
            type="number"
            min={2020}
            max={2100}
            {...register('year', { valueAsNumber: true })}
            error={errors.year?.message}
          />
          <SelectField
            label="Month"
            {...register('month', { valueAsNumber: true })}
            error={errors.month?.message}
            options={Array.from({ length: 12 }).map((_, i) => ({
              value: String(i + 1),
              label: new Date(2000, i, 1).toLocaleString('en-IN', { month: 'long' }),
            }))}
          />
        </div>
        <TextField
          label="Due date"
          type="date"
          {...register('dueDate')}
          error={errors.dueDate?.message}
        />
        <TextAreaField
          label="Notes"
          optional
          {...register('notes')}
          error={errors.notes?.message}
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={isSubmitting}>
            Create cycle
          </Button>
        </div>
      </form>
    </Drawer>
  );
}

// ---------- Bulk mark paid drawer ----------

const bulkMarkPaidFormSchema = z.object({
  paidVia: z.enum(['cash', 'bank_transfer', 'upi_offline', 'cheque', 'other']),
  paidAt: z.string().min(1, 'Required'),
  notes: z.string().max(500).optional(),
});
type BulkMarkPaidValues = z.infer<typeof bulkMarkPaidFormSchema>;

interface BulkMarkPaidDrawerProps {
  open: boolean;
  paymentIds: string[];
  onClose: () => void;
  onMarked: (count: number) => Promise<void> | void;
}

function BulkMarkPaidDrawer({ open, paymentIds, onClose, onMarked }: BulkMarkPaidDrawerProps) {
  const { toast } = useToast();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<BulkMarkPaidValues>({
    resolver: zodResolver(bulkMarkPaidFormSchema),
    defaultValues: {
      paidVia: 'cash',
      paidAt: new Date().toISOString().slice(0, 16),
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        paidVia: 'cash',
        paidAt: new Date().toISOString().slice(0, 16),
        notes: '',
      });
    }
  }, [open, reset]);

  const onSubmit = async (values: BulkMarkPaidValues) => {
    try {
      const body = feePaymentBulkMarkPaidSchema.parse({
        paymentIds,
        paidVia: values.paidVia,
        paidAt: new Date(values.paidAt).toISOString(),
        ...(values.notes ? { notes: values.notes } : {}),
      });
      const res = await apiRequest<{ ok: true; count: number }>(
        '/api/fees/payments/bulk-mark-paid',
        { method: 'POST', body },
      );
      await onMarked(res.count);
    } catch (err) {
      if (err instanceof ApiError) toast(err.message, { variant: 'error' });
      else toast('Could not mark payments as paid.', { variant: 'error' });
    }
  };

  return (
    <Drawer open={open} onClose={onClose} title={`Mark ${paymentIds.length} payments paid`} size="md">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <p className="text-sm text-[color:var(--color-text-muted)]">
          The same payment details will be applied to all selected payments.
        </p>
        <SelectField
          label="Paid via"
          {...register('paidVia')}
          error={errors.paidVia?.message}
          options={PAID_VIA_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
        />
        <TextField
          label="Paid at"
          type="datetime-local"
          {...register('paidAt')}
          error={errors.paidAt?.message}
        />
        <TextAreaField
          label="Notes"
          optional
          {...register('notes')}
          error={errors.notes?.message}
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={isSubmitting} disabled={paymentIds.length === 0}>
            Confirm
          </Button>
        </div>
      </form>
    </Drawer>
  );
}

